import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronsUpDown, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../ui/dropdown-menu'
import { useDataSource } from '../../../lib/dataSource'
import { getLastProjectId } from '../../../lib/lastProject'
import type { Project } from '../../../lib/db/models'
import { LifeSpaceColorIcon } from '../../icons/LifeSpaceColorIcon'
import { AreaIcon } from '../../icons/AreaIcon'
import { ProjectIcon } from '../ProjectIcon'
import './ProjectSwitcher.scss'

// Projects only live in the three standard spaces (DB `LifeSpace` type);
// custom spaces cannot hold projects and "all" is no project scope — neither
// appears in the space menu.
const SPACE_IDS = ['personal', 'work', 'home'] as const

export interface ProjectSwitcherProps {
  /**
   * Life space FALLBACK for the display. The switcher primarily derives the
   * shown space from the current project's own `lifeSpace` (the source of
   * truth) — the host's tab-based value can drift (tab opened from "Alle",
   * project moved to another space, stale persisted tabs).
   */
  lifeSpace: string
  /** Currently shown project (checkmarked in the list). */
  currentProjectId?: string
  /** Label on the trigger — the active project's name. */
  currentProjectName: string
  /** Pick another project — possibly in ANOTHER life space (space menu). */
  onSelect: (project: Project) => void
  /** Notifies about a life-space switch (host syncs the global active space). */
  onLifeSpaceChange?: (spaceId: string) => void
  /** Notifies when either dropdown opens/closes (toggles the titlebar drag region). */
  onOpenChange?: (open: boolean) => void
}

// Radix opens menus on pointerdown and swallows the native :active state, so
// the pressed tint never shows while opening (only when pressing to close).
// Track the press manually: down → pressed, release anywhere → unpressed.
function usePressed(): [boolean, () => void] {
  const [pressed, setPressed] = useState(false)
  useEffect(() => {
    if (!pressed) return
    const clear = () => setPressed(false)
    window.addEventListener('pointerup', clear)
    window.addEventListener('pointercancel', clear)
    return () => {
      window.removeEventListener('pointerup', clear)
      window.removeEventListener('pointercancel', clear)
    }
  }, [pressed])
  return [pressed, () => setPressed(true)]
}

/**
 * Compact split-button switcher for the list/stack titlebar:
 *
 * - LEFT segment (life-space icon): opens the life-space menu; picking a
 *   space jumps to its last-visited project (fallback: first project).
 *   Spaces without projects are disabled.
 * - RIGHT segment (project name + chevron): opens the project menu of the
 *   current space, switching in place — as before.
 *
 * Hover tints the WHOLE surface (both segments, 1px apart); the stronger
 * pressed tint stays local to the pressed segment.
 */
export function ProjectSwitcher({
  lifeSpace,
  currentProjectId,
  currentProjectName,
  onSelect,
  onLifeSpaceChange,
  onOpenChange,
}: ProjectSwitcherProps) {
  const { t } = useTranslation()
  const source = useDataSource()

  // One live list of all projects: feeds the current space's project menu,
  // the per-space disabled state AND the space-switch target resolution.
  const allProjects = source.useProjects()
  const projectsBySpace = useMemo(() => {
    const map: Record<string, Project[]> = { personal: [], work: [], home: [] }
    for (const project of allProjects ?? []) {
      // getAllProjects hides system projects but not soft-deleted ones.
      if (project.deletedAt) continue
      map[project.lifeSpace]?.push(project)
    }
    for (const list of Object.values(map)) list.sort((a, b) => a.order - b.order)
    return map
  }, [allProjects])
  // Displayed space = the CURRENT PROJECT's space (source of truth); the
  // tab-derived prop is only the fallback (e.g. while the project loads).
  // A drifted tab membership would otherwise show the wrong icon + checkmark
  // and make "switch to my actual space" a confusing no-op. Direct lookup
  // (not via allProjects) so system projects — hidden from the menus — still
  // resolve their space correctly.
  const currentProject = source.useProject(currentProjectId)
  const effectiveSpace = currentProject?.lifeSpace ?? lifeSpace
  const currentSpaceProjects = projectsBySpace[effectiveSpace] ?? []

  // Exactly ONE menu can be open (menu-bar semantics): clicking a trigger
  // opens its menu; while one is open, hovering the OTHER trigger switches to
  // it (and clicking an open menu's own trigger closes it, like macOS menus).
  // Controlled Radix state — a single slot guarantees the exclusivity.
  const [openMenu, setOpenMenu] = useState<'space' | 'project' | null>(null)
  const anyOpen = openMenu !== null
  useEffect(() => {
    onOpenChange?.(anyOpen)
  }, [anyOpen, onOpenChange])

  const spaceOpenChange = (open: boolean) =>
    setOpenMenu(prev => (open ? 'space' : prev === 'space' ? null : prev))
  const projectOpenChange = (open: boolean) =>
    setOpenMenu(prev => (open ? 'project' : prev === 'project' ? null : prev))
  // Hover-to-switch: only while the other menu is already open. The closing
  // menu must NOT restore focus to its trigger (Radix default) — that focus
  // steal would instantly dismiss the newly opened menu as "focus outside"
  // (and paint a stray focus ring on the old trigger). `switchingRef` flags
  // the handover; the onCloseAutoFocus handlers below consume it.
  const switchingRef = useRef(false)
  const hoverSwitch = (menu: 'space' | 'project') =>
    setOpenMenu(prev => {
      if (prev === null || prev === menu) return prev
      switchingRef.current = true
      return menu
    })
  const suppressCloseFocusWhileSwitching = (e: Event) => {
    if (switchingRef.current) {
      e.preventDefault()
      switchingRef.current = false
    }
  }

  const [spacePressed, pressSpace] = usePressed()
  const [projectPressed, pressProject] = usePressed()

  const handleSelectSpace = (spaceId: string) => {
    if (spaceId === effectiveSpace) return
    const candidates = projectsBySpace[spaceId] ?? []
    if (candidates.length === 0) return // disabled anyway — belt and braces
    const lastId = getLastProjectId(spaceId)
    const target = candidates.find(p => p.id === lastId) ?? candidates[0]
    onLifeSpaceChange?.(spaceId)
    onSelect(target)
  }

  return (
    <div className={`project-switcher${anyOpen ? ' project-switcher--open' : ''}`}>
      {/* LEFT: life-space menu */}
      {/* modal={false}: a modal menu puts pointer-events:none on the page —
          the other trigger would never see the hover that switches menus. */}
      <DropdownMenu open={openMenu === 'space'} onOpenChange={spaceOpenChange} modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            className={`project-switcher__segment project-switcher__space-trigger${spacePressed ? ' project-switcher__segment--pressed' : ''}`}
            aria-label={t('project.windowMode.switchLifeSpace')}
            onPointerDown={pressSpace}
            onPointerEnter={() => hoverSwitch('space')}
          >
            <LifeSpaceColorIcon lifeSpace={effectiveSpace} size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="project-switcher__menu"
          onCloseAutoFocus={suppressCloseFocusWhileSwitching}
        >
          {SPACE_IDS.map((spaceId) => (
            <DropdownMenuItem
              key={spaceId}
              className="project-switcher__item"
              disabled={(projectsBySpace[spaceId] ?? []).length === 0}
              onSelect={() => handleSelectSpace(spaceId)}
            >
              <span className="project-switcher__item-label">
                <LifeSpaceColorIcon lifeSpace={spaceId} size={16} />
                <span className="project-switcher__item-name">{t(`lifeSpaces.${spaceId}`)}</span>
              </span>
              {spaceId === effectiveSpace && <Check size={14} className="project-switcher__check" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* RIGHT: project menu (current space), as before */}
      <DropdownMenu open={openMenu === 'project'} onOpenChange={projectOpenChange} modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            className={`project-switcher__segment project-switcher__trigger${projectPressed ? ' project-switcher__segment--pressed' : ''}`}
            aria-label={t('project.windowMode.switchProject')}
            onPointerDown={pressProject}
            onPointerEnter={() => hoverSwitch('project')}
          >
            <span className="project-switcher__name">{currentProjectName}</span>
            <ChevronsUpDown size={14} className="project-switcher__chevron" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="center"
          className="project-switcher__menu"
          onCloseAutoFocus={suppressCloseFocusWhileSwitching}
        >
          {currentSpaceProjects.map((project) => (
            <DropdownMenuItem
              key={project.id}
              className="project-switcher__item"
              onSelect={() => onSelect(project)}
            >
              <span className="project-switcher__item-label">
                <ProjectIcon
                  icon={project.icon}
                  iconColor={project.iconColor}
                  size={16}
                  className="project-switcher__project-icon"
                  fallback={<AreaIcon size={16} className="project-switcher__project-icon" />}
                />
                <span className="project-switcher__item-name">{project.name}</span>
              </span>
              {project.id === currentProjectId && <Check size={14} className="project-switcher__check" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
